/**
 * Ticket Reason Dialog
 * One text box for the moderator actions that carry a sentence: Resolve (notes) and Reject (reason).
 * Replaces window.prompt, which could not mark a field required or keep the text on a failed save.
 */

import { useEffect, useState, type ReactNode } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material'
import { MAX_CLOSE_REASON_LENGTH } from './ticketActions'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'

interface TicketReasonDialogProps {
  open: boolean
  title: string
  description: string
  label: string
  confirmLabel: string
  confirmColor?: 'primary' | 'success' | 'error' | 'warning' | 'info'
  /** When true the confirm button stays disabled until something is typed. */
  required?: boolean
  submitting?: boolean
  onCancel: () => void
  onConfirm: (text: string) => void
  /** Extra controls under the text box (e.g. a checkbox the confirm carries along). */
  children?: ReactNode
}

export function TicketReasonDialog({
  open,
  title,
  description,
  label,
  confirmLabel,
  confirmColor = 'primary',
  required = false,
  submitting = false,
  onCancel,
  onConfirm,
  children,
}: TicketReasonDialogProps) {
  const [text, setText] = useState('')
  const mobileDialog = useMobileDialogProps()

  // A fresh box each time it opens.
  useEffect(() => {
    if (open) setText('')
  }, [open])

  const trimmed = text.trim()
  const blocked = submitting || (required && !trimmed)

  return (
    <Dialog open={open} onClose={submitting ? undefined : onCancel} fullWidth maxWidth="sm" {...mobileDialog}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{description}</DialogContentText>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          label={label}
          required={required}
          value={text}
          onChange={e => setText(e.target.value)}
          disabled={submitting}
          inputProps={{ maxLength: MAX_CLOSE_REASON_LENGTH }}
        />
        {children}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" color={confirmColor} onClick={() => onConfirm(trimmed)} disabled={blocked}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
